import { useMatomo } from '@datapunt/matomo-tracker-react';
import cx from 'clsx';
import { format } from 'date-fns';
import { saveAs } from 'file-saver';
import React, { useEffect, useRef } from 'react';

import { ReactComponent as TableSortIcon } from '@assets/svg/table-sort.svg';

import ButtonGoUp from '@components/common/ButtonGoUp/ButtonGoUp';
import Header from '@components/common/Header/Header';
import BtnHeaderDownload from '@components/common/Header/components/BtnHeaderDownload/BtnHeaderDownload';
import ResetButton from '@components/common/ResetButton/ResetButton';
import SkeletonBlock from '@components/common/Skeleton/SkeletonBlock';
import TableEmptyRow from '@components/common/TableEmptyRow/TableEmptyRow';
import TooltipSort from '@components/common/TooltipSort/TooltipSort';
import DatePicker from '@components/form/DatePicker/DatePicker';
import Tabs from '@components/uikit/Tabs/Tabs';

import { initialSorting } from '@constants/tableSorting';

import { getFailureCauseCategoriesStatusTabItems } from '@helpers/getFailureCauseCategoriesStatusTabItems';
import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { selectAppLocationSelectValue, selectAppRtl } from '@store/useAppStore';

import ExpandedRow from './components/ExpandedRow/ExpandedRow';
import FailureCauseCategoriesTableSkeleton from './components/FailureCauseCategoriesTableSkeleton/FailureCauseCategoriesTableSkeleton';
import { useFailureCauseCategoriesListQuery } from './services/useFailureCauseCategoriesListQuery';
import { useSaveFailureCauseCategoriesInfoMutation } from './services/useSaveFailureCauseCategoriesInfoQuery';
import {
  selectFailureCauseCategoriesDateFilter,
  selectFailureCauseCategoriesSortingFilter,
  selectFailureCauseCategoriesStatusFilter,
  useFailureCauseCategoriesActions,
} from './store/useFailureCauseCategoriesStore';
import style from './style.module.scss';

const FailureCauseCategories = () => {
  const failureCauseCategoriesTableContainerRef = useRef(null);

  const rtl = selectAppRtl();
  const { trackPageView } = useMatomo();
  const { t } = useTranslation();
  const { setStatusFilter, setSorting, setDateRangeFilterDate } = useFailureCauseCategoriesActions();
  const { dateFrom, dateTo } = selectFailureCauseCategoriesDateFilter();
  const { data: profileData } = useUserProfileQuery();
  const { mutate: saveFailureCauseCategoriesInfoPdfMutation } = useSaveFailureCauseCategoriesInfoMutation();

  const locationSelectValue = selectAppLocationSelectValue();
  const statusFilter = selectFailureCauseCategoriesStatusFilter();
  const sorting = selectFailureCauseCategoriesSortingFilter();

  const {
    data = [],
    isLoading,
    isFetching,
  } = useFailureCauseCategoriesListQuery({
    locationIds:
      locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue?.filterIds,
    sorting,
    statusType: statusFilter,
    dateFrom,
    dateTo,
    enabled: dateFrom || dateTo ? !!dateFrom && !!dateTo : true,
  });

  const currentDateFormat = `${profileData?.dateFormat.date} ${profileData?.dateFormat.time}`;

  const onChangeDateRange = (dates: [Date | null, Date | null]) => {
    setDateRangeFilterDate({
      dateFrom: dates[0] ? format(dates[0], 'yyyy-MM-dd HH:mm:ss') : undefined,
      dateTo: dates[1] ? format(dates[1], 'yyyy-MM-dd HH:mm:ss') : undefined,
    });
  };

  const clearFilter = () => {
    setDateRangeFilterDate({
      dateFrom: undefined,
      dateTo: undefined,
    });
  };

  const downloadFailureCauseCategoriesInfoPdf = () => {
    showToast({ variant: 'success', message: t('The download will begin shortly.') });

    saveFailureCauseCategoriesInfoPdfMutation(
      {
        locationIds:
          locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue.filterIds,
        sorting,
        statusType: statusFilter,
        dateFrom,
        dateTo,
      },
      {
        onSuccess: (data) => {
          saveAs(
            new Blob([data?.data], { type: 'text/plain;charset=utf-8' }),
            `Failure Cause Categories ${format(new Date(), currentDateFormat)}.xls`,
          );
        },
      },
    );
  };
  const failureCauseCategoriesStatusTabItems = getFailureCauseCategoriesStatusTabItems(t);

  useEffect(() => {
    trackPageView({});
  }, []);

  const onChangeSorting = (key: string) => {
    if (!sorting.id || sorting.id !== key) {
      setSorting({ id: key, desc: false });
      return;
    }
    const { id, desc } = sorting;
    if (desc) {
      setSorting(initialSorting);
    } else {
      setSorting({ id, desc: true });
    }
  };

  return (
    <>
      <Header
        title={t('Failure Cause Categories')}
        isSecondaryPage
        showLogo={true}
        showFacilitySelect={true}
        headerRightComponent={
          <BtnHeaderDownload
            tooltipInfo={t('Download Failure Cause Categories')}
            downloadFn={downloadFailureCauseCategoriesInfoPdf}
          />
        }
      />
      <div className={style.failureCauseCategories}>
        <div className={style.filtersBlock}>
          {isLoading ? (
            <SkeletonBlock className={style.skeletonTab} />
          ) : (
            <>
              <Tabs
                label={t('Type')}
                id="top-failure-cause-categories-type"
                value={statusFilter}
                tabsItems={failureCauseCategoriesStatusTabItems}
                type="woHelperText"
                onChange={setStatusFilter}
                variant="horizontal"
              />
              <div className={style.dateRangeFilter}>
                <DatePicker
                  id="daterange-failure-cause-category"
                  placeholderText={t('Select Date Range')}
                  dateFormat={rtl ? 'yyyy/MM/dd' : `${profileData?.dateFormat.date}`}
                  startDate={dateFrom ? new Date(dateFrom) : undefined}
                  endDate={dateTo ? new Date(dateTo) : undefined}
                  onChange={(values: [Date | null, Date | null]) => onChangeDateRange(values)}
                  minDate={new Date(profileData?.customer.created_at)}
                  maxDate={new Date()}
                  selectsRange
                  showMonthDropdown
                  showYearDropdown
                  type={'woHelperText'}
                />
              </div>
              <ResetButton showResetButton={!!dateFrom || !!dateTo} onClick={clearFilter} />
            </>
          )}
        </div>
        <div
          className={style.failureCauseCategoriesTableWrapper}
          ref={failureCauseCategoriesTableContainerRef}
        >
          <table className={style.failureCauseCategoriesTable}>
            <thead>
              <tr>
                <th className={style.tableHead}>
                  {t('Failure Cause Categories')}
                  <TableSortIcon
                    className={cx(style.sortIcon, { [style.activeSort]: sorting.id === 'fault_name' })}
                    onClick={() => onChangeSorting('fault_name')}
                    data-tooltip-content="fault_name"
                    data-tooltip-id="failure-cause-categories-sort-tooltip"
                  />
                </th>
                <th className={style.tableHead}>
                  {t('Total')}
                  <TableSortIcon
                    className={cx(style.sortIcon, {
                      [style.activeSort]: sorting.id === 'action_items_count',
                    })}
                    onClick={() => onChangeSorting('action_items_count')}
                    data-tooltip-content="action_items_count"
                    data-tooltip-id="failure-cause-categories-sort-tooltip"
                  />
                </th>
                <th className={style.tableHead} />
              </tr>
            </thead>
            <tbody>
              {isFetching ? (
                <FailureCauseCategoriesTableSkeleton />
              ) : data?.length ? (
                data?.map((row) => <ExpandedRow row={row} key={row.fault_id} />)
              ) : (
                <TableEmptyRow text={t('No failure cause categories were found.')} />
              )}
            </tbody>
          </table>
          <TooltipSort id="failure-cause-categories-sort-tooltip" sorting={sorting} />
        </div>
        <div className={style.tableFooter}>
          <ButtonGoUp ref={failureCauseCategoriesTableContainerRef} />
        </div>
      </div>
    </>
  );
};

export default FailureCauseCategories;
